import React from 'react';
import { cn } from '@/shared/lib/utils';
import Select, {
  FilterOptionOption,
  MultiValue,
  OptionProps,
  SingleValue,
  StylesConfig,
  components,
} from 'react-select';
import './react-select-dark.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  description?: string;
}

export interface ReactSelectProps {
  options: SelectOption[];
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  multiple?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  size?: 'sm' | 'md' | 'lg';
  maxHeight?: number;
  loading?: boolean;
  noOptionsMessage?: string;
  createOption?: (inputValue: string) => SelectOption;
  onCreateOption?: (option: SelectOption) => void;
  onSearch?: (query: string) => void;
  minSearchLength?: number;
  filterOption?: (option: FilterOptionOption<SelectOption>, rawInput: string) => boolean;
}

// Estilos customizados para tema escuro
const darkThemeStyles: StylesConfig<SelectOption, boolean> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderColor: state.isFocused ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
    borderWidth: '1px',
    minHeight: '40px',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(255, 255, 255, 0.1)' : 'none',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    color: 'white',
  }),
  input: (base) => ({
    ...base,
    color: 'white',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.5)',
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#2A2A2A',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '6px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
    color: 'white',
    padding: '8px 12px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    margin: '2px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'white',
    padding: '4px 8px',
    fontSize: '13px',
    fontWeight: '500',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '0 6px 6px 0',
    padding: '4px 6px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
    },
  }),
  loadingIndicator: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.6)',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.5)',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.5)',
    '&:hover': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'rgba(255, 255, 255, 0.5)',
    padding: '8px 12px',
  }),
};

export const ReactSelect: React.FC<ReactSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Selecione uma opção",
  className = "",
  disabled = false,
  error = false,
  errorMessage,
  multiple = false,
  searchable = false,
  clearable = true,
  size = "md",
  maxHeight = 200,
  loading = false,
  noOptionsMessage = "Nenhuma opção encontrada",
  createOption,
  onCreateOption,
  onSearch,
  minSearchLength = 0,
  filterOption,
}) => {
  // Converter valor para formato do react-select
  const getSelectValue = () => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      return options.filter(option => values.includes(option.value));
    } else {
      const singleValue = Array.isArray(value) ? value[0] : value;
      return options.find(option => option.value === singleValue) || null;
    }
  };

  // Handler para mudanças
  const handleChange = (selectedOption: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
    if (multiple) {
      const values = Array.isArray(selectedOption)
        ? selectedOption.map(option => option.value)
        : [];
      onChange(values);
    } else {
      const singleOption = selectedOption as SingleValue<SelectOption>;
      onChange(singleOption?.value || "");
    }
  };

  // Handler para busca
  const handleInputChange = (inputValue: string) => {
    if (onSearch && inputValue.length >= minSearchLength) {
      onSearch(inputValue);
    }
  };

  // Componente customizado para opções com ícones
  const Option = (props: OptionProps<SelectOption>) => (
    <components.Option {...props}>
      <div className="flex items-center gap-2">
        {props.data.icon && (
          <div className="flex-shrink-0">
            {props.data.icon}
          </div>
        )}
        <div className="flex-1">
          <div className="text-white">{props.data.label}</div>
          {props.data.description && (
            <div className="text-xs text-gray-400">{props.data.description}</div>
          )}
        </div>
      </div>
    </components.Option>
  );

  // Tamanhos
  const sizeStyles = {
    sm: { minHeight: '32px', fontSize: '12px' },
    md: { minHeight: '40px', fontSize: '14px' },
    lg: { minHeight: '48px', fontSize: '16px' }
  };

  // Estilos customizados baseados no tamanho
  const customStyles: StylesConfig<SelectOption, boolean> = {
    ...darkThemeStyles,
    control: (base, state) => ({
      ...darkThemeStyles.control!(base, state),
      ...sizeStyles[size],
    }),
  };

  return (
    <div className={cn("relative w-full", className)}>
      <Select
        isMulti={multiple}
        options={options}
        value={getSelectValue()}
        onChange={handleChange}
        onInputChange={handleInputChange}
        placeholder={placeholder}
        isDisabled={disabled}
        isLoading={loading}
        isClearable={clearable}
        isSearchable={searchable}
        noOptionsMessage={() => noOptionsMessage}
        styles={customStyles}
        components={{ Option }}
        maxMenuHeight={maxHeight}
        className="react-select-container"
        classNamePrefix="react-select"
        filterOption={
          filterOption ??
          ((option, rawInput) => {
            const query = rawInput.trim().toLowerCase();
            if (!query) return true;
            const label = option.data.label?.toLowerCase() ?? '';
            const description = option.data.description?.toLowerCase() ?? '';
            return label.includes(query) || description.includes(query);
          })
        }
        formatOptionLabel={(option) => (
          <div className="flex items-center gap-2">
            {option.icon && (
              <div className="flex-shrink-0">
                {option.icon}
              </div>
            )}
            <div className="flex-1">
              <div className="text-white">{option.label}</div>
              {option.description && (
                <div className="text-xs text-gray-400">{option.description}</div>
              )}
            </div>
          </div>
        )}
        // Suporte para criação de opções
        {...(createOption && {
          isCreatable: true,
          onCreateOption: (inputValue: string) => {
            const newOption = createOption(inputValue);
            if (onCreateOption) {
              onCreateOption(newOption);
            }
            // Adiciona a nova opção à lista
            const updatedOptions = [...options, newOption];
            if (multiple) {
              const currentValues = Array.isArray(value) ? value : [];
              onChange([...currentValues, newOption.value]);
            } else {
              onChange(newOption.value);
            }
          }
        })}
      />

      {/* Error Message */}
      {error && errorMessage && (
        <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
      )}
    </div>
  );
};
